import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Account, getExternalPlatformIdFromInternalPlatformId, getPlatformIdFromPlatform, notEmpty, OperationType, Platform, validateAndFormatAddress, } from "@orb-labs/types";
import { CreateOperationsStatus } from "@orb-labs/orby-core";
import { useOrby } from "../components/OrbyProvider/OrbyProvider.js";
import { useVirtualNode } from "../hooks/useVirtualNode.js";
import { usePlatformStorage } from "../storage/PlatformStorageContext.js";
import { useBullrunRuntime } from "../runtime/BullrunRuntimeContext.js";
const PolymarketContext = createContext(undefined);
export const usePolymarket = () => {
    const context = useContext(PolymarketContext);
    if (!context) {
        throw new Error("usePolymarket must be used within a PolymarketProvider");
    }
    return context;
};
export const PolymarketProvider = ({ children }) => {
    const { signTypedData: signTypedDataFromRuntime } = useBullrunRuntime();
    const storage = usePlatformStorage();
    const { accountCluster } = useOrby();
    const [isSetupComplete, setIsSetupComplete] = useState(false);
    const [isSettingUp, setIsSettingUp] = useState(false);
    const setupAttemptedFor = useRef(new Set());
    const addressesWithCredentials = useRef(new Set());
    // Single-flight, keyed by wallet address. Concurrent ensureSetup callers for
    // the same address share the in-flight promise; calls for different addresses
    // (e.g. account switch mid-setup) get their own promise so a setup started
    // for A is never reused as the result for B.
    const inflightSetupByAddress = useRef(new Map());
    // Latest-known formatted address for the active account. Async runSetup
    // callbacks read this to bail out of state writes if the user switched
    // accounts mid-setup — without the guard, setters fire for the old address
    // on a provider that's now rendering the new one.
    const currentAddressRef = useRef(undefined);
    const polygonPlatformId = useMemo(() => BigInt(getPlatformIdFromPlatform(Platform.POLYGON)), []);
    const polymarketPlatformId = useMemo(() => BigInt(getPlatformIdFromPlatform(Platform.POLYMARKET)), []);
    const polymarketAccount = useMemo(() => Account.selectAccountForPlatform(accountCluster?.accounts ?? [], polygonPlatformId), [accountCluster?.accounts, polygonPlatformId]);
    currentAddressRef.current = validateAndFormatAddress(polymarketAccount?.address);
    const { virtualNode } = useVirtualNode(polymarketAccount?.address, polygonPlatformId);
    const ensureSetup = useCallback(() => {
        const address = validateAndFormatAddress(polymarketAccount?.address);
        if (!address)
            return Promise.resolve(false);
        const existing = inflightSetupByAddress.current.get(address);
        if (existing) {
            return existing;
        }
        const runSetup = async () => {
            const commitIfStillCurrent = (fn) => {
                if (currentAddressRef.current === address)
                    fn();
            };
            try {
                // Verify the in-memory dedup cache against storage before trusting it.
                // A decrypt failure on a prior read can leave a cached "attempted" flag
                // without the underlying credentials, so the cheap has* check (no key
                // derivation on web, no biometric prompt on mobile) tells us whether
                // to trust the cache or fall through to a fresh setup.
                if (setupAttemptedFor.current.has(address)) {
                    const hasCredentials = await storage.hasPolymarketL2Credentials(address);
                    if (hasCredentials) {
                        commitIfStillCurrent(() => setIsSetupComplete(true));
                        return true;
                    }
                    setupAttemptedFor.current.delete(address);
                    addressesWithCredentials.current.delete(address);
                    // Fall through to a fresh setup.
                }
                const cached = await storage.getPolymarketL2Credentials(address);
                if (cached) {
                    setupAttemptedFor.current.add(address);
                    addressesWithCredentials.current.add(address);
                    commitIfStillCurrent(() => setIsSetupComplete(true));
                    return true;
                }
                if (!virtualNode || !polymarketAccount)
                    return false;
                commitIfStillCurrent(() => setIsSettingUp(true));
                const operationSet = await virtualNode.getOperationsToSetupOnPlatform(polymarketPlatformId, polymarketAccount);
                if (!operationSet || operationSet.status !== CreateOperationsStatus.SUCCESS) {
                    console.error("[PolymarketProvider] getOperationsToSetupOnPlatform failed", {
                        status: operationSet?.status,
                        errorMessage: operationSet?.errorMessage,
                    });
                    return false;
                }
                const allOps = [];
                if (operationSet.intents) {
                    for (const intent of operationSet.intents) {
                        if (intent.intentOperations) {
                            allOps.push(...intent.intentOperations);
                        }
                    }
                }
                if (notEmpty(operationSet.primaryOperation)) {
                    allOps.push(operationSet.primaryOperation);
                }
                const l2SetupOp = allOps.find((op) => op.type === OperationType.POLYMARKET_L2_SETUP);
                if (!l2SetupOp) {
                    setupAttemptedFor.current.add(address);
                    commitIfStillCurrent(() => setIsSetupComplete(true));
                    return true;
                }
                const parsed = JSON.parse(l2SetupOp.data);
                const { typedData } = parsed;
                const signOp = {
                    from: address,
                    platformId: polygonPlatformId,
                    data: JSON.stringify(typedData),
                };
                const signature = await signTypedDataFromRuntime(signOp);
                if (!signature) {
                    console.warn("[PolymarketProvider] User declined ClobAuth signature");
                    return false;
                }
                const externalPlatformId = getExternalPlatformIdFromInternalPlatformId(polymarketPlatformId);
                if (!notEmpty(externalPlatformId)) {
                    console.error("[PolymarketProvider] Could not resolve external platform ID for Polymarket");
                    return false;
                }
                const credentials = await virtualNode.submitOperationsToSetupOnPlatform({
                    platformId: externalPlatformId,
                    address,
                    operations: [{ signature, typedDataJson: JSON.stringify(typedData) }],
                });
                if (!credentials) {
                    console.error("[PolymarketProvider] Setup submission returned no credentials");
                    return false;
                }
                const stored = await storage.storePolymarketL2Credentials(address, credentials);
                if (stored) {
                    setupAttemptedFor.current.add(address);
                    addressesWithCredentials.current.add(address);
                    commitIfStillCurrent(() => setIsSetupComplete(true));
                }
                return stored;
            }
            catch (error) {
                console.error("[PolymarketProvider] Setup failed", error);
                return false;
            }
            finally {
                commitIfStillCurrent(() => setIsSettingUp(false));
            }
        };
        const promise = runSetup().finally(() => {
            inflightSetupByAddress.current.delete(address);
        });
        inflightSetupByAddress.current.set(address, promise);
        return promise;
    }, [signTypedDataFromRuntime, virtualNode, polymarketAccount, polygonPlatformId, polymarketPlatformId, storage]);
    const reset = useCallback(async () => {
        for (const addr of addressesWithCredentials.current) {
            await storage.clearPolymarketL2Credentials(addr);
        }
        addressesWithCredentials.current.clear();
        setupAttemptedFor.current.clear();
        setIsSetupComplete(false);
    }, [storage]);
    useEffect(() => {
        const address = validateAndFormatAddress(polymarketAccount?.address);
        if (!address) {
            reset().catch((err) => console.error("[PolymarketProvider] reset failed", err));
            return;
        }
        // Account changed to a new wallet: drop in-progress UI flags and restore
        // the completion flag from the per-address cache. Keeping setupAttemptedFor
        // intact lets a switch back to a previously-set-up account skip re-setup.
        setIsSettingUp(false);
        setIsSetupComplete(setupAttemptedFor.current.has(address));
    }, [polymarketAccount, reset]);
    const signTypedData = useCallback(async (operation, orderType) => {
        if (operation.platformId === polymarketPlatformId) {
            // Setup must run for the account the operation targets. ensureSetup
            // derives the address from polymarketAccount, so the op must come from
            // the same wallet — otherwise we'd set up creds for one address and
            // then try to load them for another, producing a silently-broken order.
            const expectedAddress = validateAndFormatAddress(polymarketAccount?.address);
            const operationAddress = validateAndFormatAddress(operation.from);
            if (!notEmpty(expectedAddress) || !notEmpty(operationAddress)) {
                console.error("[PolymarketProvider] Missing or invalid address for signTypedData", {
                    expected: polymarketAccount?.address,
                    received: operation.from,
                });
                return undefined;
            }
            if (expectedAddress !== operationAddress) {
                console.error("[PolymarketProvider] Operation address does not match active Polymarket account", {
                    expected: expectedAddress,
                    received: operationAddress,
                });
                return undefined;
            }
            // Bail before prompting for the order signature if setup didn't succeed —
            // signing without L2 creds would just produce a JSON the backend rejects.
            const ok = await ensureSetup();
            if (!ok)
                return undefined;
            const signature = await signTypedDataFromRuntime(operation);
            if (!signature)
                return undefined;
            const polymarketL2Creds = await storage.getPolymarketL2Credentials(expectedAddress);
            if (!notEmpty(polymarketL2Creds)) {
                // ensureSetup returned true, so credentials should be readable. If
                // we still can't load them (storage corruption, key-derivation
                // failure, race with logout), fail loud instead of sending an order
                // the backend will silently reject.
                console.error("[PolymarketProvider] L2 credentials missing after successful setup", {
                    address: expectedAddress,
                });
                return undefined;
            }
            return JSON.stringify({ signature, polymarketL2Creds, orderType });
        }
        return signTypedDataFromRuntime(operation);
    }, [signTypedDataFromRuntime, ensureSetup, polymarketPlatformId, polymarketAccount, storage]);
    const value = useMemo(() => ({
        isSetupComplete,
        isSettingUp,
        signTypedData,
        ensureSetup,
        reset,
        polymarketAccount,
        polygonPlatformId,
    }), [isSetupComplete, isSettingUp, signTypedData, ensureSetup, reset, polymarketAccount, polygonPlatformId]);
    return _jsx(PolymarketContext.Provider, { value: value, children: children });
};
